import type { Course, CourseEdition } from "@/lib/api";

export type ListingFilterState = {
  search: string;
  courseId: string;
  editionId: string;
  category: string;
  month: string;
};

export const emptyListingFilters = (): ListingFilterState => ({
  search: "",
  courseId: "",
  editionId: "",
  category: "",
  month: "",
});

export function buildMonthOptions(count = 24): { value: string; label: string }[] {
  const opts: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth() - i, 1));
    const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("es-MX", { month: "long", year: "numeric", timeZone: "UTC" });
    opts.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return opts;
}

/** Coincide YYYY-MM con una fecha ISO o Date-like. */
export function matchesMonth(dateValue: string | Date | null | undefined, month: string): boolean {
  if (!month) return true;
  if (!dateValue) return false;
  const iso = typeof dateValue === "string" ? dateValue : dateValue.toISOString();
  return iso.slice(0, 7) === month;
}

export function uniqueCategories(courses: Pick<Course, "category">[]): string[] {
  return Array.from(new Set(courses.map((c) => c.category).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, "es")
  );
}

export function editionsForFilters(
  editions: CourseEdition[],
  filters: Pick<ListingFilterState, "courseId" | "category">,
  coursesById?: Map<string, Course>
): CourseEdition[] {
  return editions.filter((ed) => {
    if (filters.courseId && ed.course_id !== filters.courseId) return false;
    if (filters.category) {
      const cat = ed.course?.category ?? coursesById?.get(ed.course_id)?.category;
      if (cat !== filters.category) return false;
    }
    return true;
  });
}

export function hasActiveListingFilters(
  filters: ListingFilterState,
  extras: Record<string, string | boolean | undefined> = {}
): boolean {
  if (filters.search.trim() || filters.courseId || filters.editionId || filters.category || filters.month) {
    return true;
  }
  return Object.values(extras).some((v) => {
    if (typeof v === "boolean") return v;
    return Boolean(v && String(v).trim());
  });
}

export function editionOptionLabel(ed: CourseEdition, showCourseName = true): string {
  const base = ed.edition_label || ed.location || "Edición";
  if (showCourseName && ed.course?.name) return `${base} — ${ed.course.name}`;
  return base;
}
